// src/pages/FreeDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, Line } from "react-chartjs-2";
import "chart.js/auto";

import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, onSnapshot, updateDoc, arrayRemove } from "firebase/firestore";
import { getStorage, ref, deleteObject } from "firebase/storage";

import { Tabs, Tab, useMediaQuery } from "@mui/material";
import "../App.css";

// Singletons
import { auth, db, callFunction } from "../firebase";

/* ---------- Helpers ---------- */
function asDateMaybe(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === "number") return new Date(v > 1e12 ? v : v * 1000);
  if (v?._seconds != null) return new Date(v._seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}
function formatDate(d) {
  if (!d) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
function normalizeBilling(interval) {
  const iv = String(interval || "").toLowerCase();
  if (iv === "year" || iv === "annual" || iv === "yearly") return "annual";
  if (iv === "month" || iv === "monthly") return "monthly";
  return "—";
}
function defaultCapFromPackageKey(k) {
  const key = String(k || "").toLowerCase();
  if (!key || key === "free") return 5; // ✅ default Free = 5
  if (key.includes("personal")) return 60;
  if (key.includes("family_lite")) return 90;
  if (key.includes("family")) return 180;
  return 0;
}


function addMonths(d, n) {
  const copy = new Date(d.getTime());
  copy.setMonth(copy.getMonth() + n);
  return copy;
}

function computeFreeWindowFromCreatedAt(u) {
  const now = new Date();

  let periodStart =
    asDateMaybe(u?.usagePeriodStart) ||
    asDateMaybe(u?.createdAt) ||
    now;

  let periodEnd = addMonths(periodStart, 1);

  // roll forward if we've passed the end
  while (now >= periodEnd) {
    periodStart = periodEnd;
    periodEnd = addMonths(periodStart, 1);
  }

  return { periodStart, periodEnd };
}



/** Client-side fallback compute (used only if getUsage HTTP fails) */
function computeUsageFallback(u) {
  const now = new Date();

  // 1) Work out the current period window for this user
  // Prefer an explicit usagePeriodStart field; fall back to createdAt; finally 'now'
  let periodStart =
    asDateMaybe(u?.usagePeriodStart) ||
    asDateMaybe(u?.createdAt) ||
    now;

  let periodEnd = addMonths(periodStart, 1);

  // If we've moved past the end, roll the window forward and reset used
  let usedRaw = Number(u?.usageUsed || 0);
  while (now >= periodEnd) {
    periodStart = periodEnd;
    periodEnd = addMonths(periodStart, 1);
    usedRaw = 0; // new period → fresh allowance
  }

  // 2) Determine cap (included stories)
  const sub = u?.subscription || null;
  const ent = u?.entitlements || {};
  const planLegacyObj = typeof u?.plan === "object" ? u.plan : null;
  const planString = typeof u?.plan === "string" ? u.plan : null;

  let cap =
    Number(
      ent?.storyQuotaPerMonth ??
        (planLegacyObj?.capStories != null ? planLegacyObj.capStories : 0)
    ) || 0;

  if (!cap) {
    const key = sub?.packageKey || planString || "free";
    cap = defaultCapFromPackageKey(key); // → Free = 3
  }

  // 3) Add-ons remaining (non-expired)
  const nowMs = Date.now();
  const addonsRaw = Array.isArray(u?.addons) ? u.addons : [];
  const remainingAddons = addonsRaw
    .filter((a) => {
      if (!a?.expiresAt) return true;
      const ms =
        a.expiresAt._seconds != null
          ? a.expiresAt._seconds * 1000
          : new Date(a.expiresAt).getTime();
      return ms > nowMs;
    })
    .reduce((sum, a) => sum + Number(a?.remaining || 0), 0);

  // 4) Remaining balances
  const remainingIncluded = Math.max(cap - usedRaw, 0);
  const remainingTotal = remainingIncluded + remainingAddons;

  // 5) Display / labels
  const currentPeriodEnd = periodEnd;
  const billing =
    normalizeBilling(sub?.billingInterval) ||
    normalizeBilling(planLegacyObj?.billing) ||
    "—";

  const planLabel =
    sub?.displayName || planString || planLegacyObj?.id || "Free";

  return {
    cap,
    used: usedRaw,
    remainingIncluded,
    remainingAddons,
    remainingTotal,
    currentPeriodEnd,
    billing,
    planLabel,
    source: "fallback",
    // optional if you ever want to show it
    periodStart,
  };
}

const FreeDashboard = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:768px)");

  // 🔁 If this signed-in user is a school admin/teacher with an org/school, bounce them to the school dashboard.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) return;
      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        const user = snap.exists() ? snap.data() : null;
        const role = String(user?.role || "").toLowerCase();
        const orgId = user?.orgId || user?.schoolId || null;
        if ((role === "school_admin" || role === "teacher") && orgId) {
          try { localStorage.setItem("schoolId", orgId); } catch {}
          navigate("/school-dashboard", { replace: true });
        }
      } catch {
        // ignore
      }
    });
    return () => unsub();
  }, [navigate]);

  const [userDoc, setUserDoc] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [diversityMode, setDiversityMode] = useState(
    () => localStorage.getItem("diversityMode") === "true"
  );
 
  const [generating, setGenerating] = useState(false);
const [summaryRange, setSummaryRange] = useState("month"); // "month" | "all"



  // Usage from server
  const [usage, setUsage] = useState({
    cap: null,
    used: null,
    remainingIncluded: null,
    remainingAddons: null,
    remainingTotal: null,
    currentPeriodEnd: null,
    billing: "—",
    planLabel: "—",
  });

  
  const planLabelLower = String(usage.planLabel || "").toLowerCase();
  const isFreePlan = planLabelLower.includes("free");

  // For Free plan, compute a local monthly window if backend didn't send one
  const fallbackFreeWindow = useMemo(() => {
    if (!isFreePlan || !userDoc) return null;
    return computeFreeWindowFromCreatedAt(userDoc);
  }, [isFreePlan, userDoc]);

  const effectiveCurrentPeriodEnd =
    usage.currentPeriodEnd || fallbackFreeWindow?.periodEnd || null;
  const [usageLoading, setUsageLoading] = useState(true);

  // Live auth + user doc
  useEffect(() => {
  const offAuth = onAuthStateChanged(auth, (user) => {
    if (!user) {
      setUserDoc(null);
      setUsageLoading(false);
      return;
    }

    const ref = doc(db, "users", user.uid);
    const offDoc = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setUserDoc(data);

        
      },
      (err) => {
        console.error("user snapshot error:", err);
      }
    );
    return () => offDoc();
  });
  return () => offAuth();
}, []);




  // Diversity toggle cross-tab sync
  useEffect(() => {
    const onStorage = () => setDiversityMode(localStorage.getItem("diversityMode") === "true");
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Fetch usage from server; fallback to client compute on failure
  useEffect(() => {
    let cancelled = false;

    async function loadUsage() {
      if (!auth.currentUser) {
        setUsageLoading(false);
        return;
      }
      setUsageLoading(true);
      try {
        const v = await callFunction("getUsage", {});
        if (cancelled) return;

        setUsage({
          cap: v.cap ?? null,
          used: v.used ?? null,
          remainingIncluded: v.remainingIncluded ?? null,
          remainingAddons: v.remainingAddons ?? null,
          remainingTotal:
            v.remainingTotal ?? ((v.remainingIncluded ?? 0) + (v.remainingAddons ?? 0)),
          currentPeriodEnd: asDateMaybe(v.currentPeriodEnd) || null,
          billing: v.billing || "—",
          planLabel: v.planLabel || "Free",
        });
      } catch (e) {
        console.warn("getUsage failed, using fallback:", e?.message || e);
        const fb = computeUsageFallback(userDoc || {});
        if (cancelled) return;
        setUsage(fb);
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    }

    loadUsage();
    return () => {
      cancelled = true;
    };
  }, [userDoc]);

  // Derive stats for charts
  const last7Days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - i);
      return d.toISOString().split("T")[0];
    }).reverse();
  }, []);
  const last6Weeks = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 6 }, (_, index) => {
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay() - index * 7);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return {
        label: `${start.getDate()} ${start.toLocaleString("default", { month: "short" })} - ${end.getDate()} ${end.toLocaleString("default", { month: "short" })}`,
        start,
        end,
      };
    }).reverse();
  }, []);

  const storiesReadData = useMemo(
    () => ({
      labels: last7Days,
      datasets: [
        {
          label: "Stories Read Per Day",
          data: last7Days.map((d) => {
            const arr = Array.isArray(userDoc?.readingActivity) ? userDoc.readingActivity : [];
            const e = arr.find((x) => x.date === d);
            return e ? e.storiesGenerated || 0 : 0;
          }),
          backgroundColor: "rgba(0,123,255,0.5)",
          borderColor: "rgba(0,123,255,1)",
          borderWidth: 2,
        },
      ],
    }),
    [last7Days, userDoc]
  );

  const timeSpentReadingData = useMemo(
    () => ({
      labels: last7Days,
      datasets: [
        {
          label: "Total Reading Time Per Day (Minutes)",
          data: last7Days.map((d) => {
            const arr = Array.isArray(userDoc?.readingActivity) ? userDoc.readingActivity : [];
            const e = arr.find((x) => x.date === d);
            return e ? Math.round((e.timeSpent || 0) / 60) : 0;
          }),
          backgroundColor: "rgba(255,99,132,0.5)",
          borderColor: "rgba(255,99,132,1)",
          borderWidth: 2,
        },
      ],
    }),
    [last7Days, userDoc]
  );

  const weeklyReadingData = useMemo(() => {
    const arr = Array.isArray(userDoc?.readingActivity) ? userDoc.readingActivity : [];
    return last6Weeks.map((w) => {
      return (
        arr.filter((e) => {
          const d = new Date(e.date);
          return d >= w.start && d <= w.end;
        }).length || 0
      );
    });
  }, [userDoc, last6Weeks]);

  const readingStreaksData = useMemo(
    () => ({
      labels: last6Weeks.map((w) => w.label),
      datasets: [
        {
          label: "Reading Streak (Days per Week)",
          data: weeklyReadingData,
          backgroundColor: "rgba(255,165,0,0.6)",
          borderColor: "rgba(255,165,0,1)",
          borderWidth: 2,
        },
      ],
    }),
    [weeklyReadingData, last6Weeks]
  );

  const favoriteGenresData = useMemo(() => {
  const raw = userDoc?.favoriteGenres || {};

  // ✅ Only keep genres where the value is a number (valid count)
  const entries = Object.entries(raw).filter(
    ([key, val]) => typeof val === "number" && val > 0
  );

  const labels = entries.map(([genre]) => genre);
  const data = entries.map(([_, count]) => count);

  return {
    labels,
    datasets: [
      {
        label:
          userDoc?.fullName ||
          (userDoc?.email || "").split("@")[0] ||
          "You",
        data,
        backgroundColor: "rgba(0,123,255,0.6)",
        borderColor: "rgba(0,123,255,1)",
        borderWidth: 2,
      },
    ],
  };
}, [userDoc]);


  const averageReadingTimeData = useMemo(
    () => ({
      labels: last7Days,
      datasets: [
        {
          label: "Average Reading Time Per Session (Minutes)",
          data: last7Days.map((d) => {
            const arr = Array.isArray(userDoc?.readingActivity) ? userDoc.readingActivity : [];
            const e = arr.find((x) => x.date === d);
            if (e && e.storiesGenerated > 0) {
              return Math.round((e.timeSpent || 0) / e.storiesGenerated / 60);
            }
            return 0;
          }),
          backgroundColor: "rgba(0,128,0,0.5)",
          borderColor: "rgba(0,128,0,1)",
          borderWidth: 2,
        },
      ],
    }),
    [last7Days, userDoc]
  );

  const userLoading = userDoc === null;



  /* ---------- Actions ---------- */
  const handleGenerateStory = async () => {
  const user = auth.currentUser;
 if (!user) {
  alert("Please sign in to create stories.");
  return;
}

  if (usage.remainingTotal <= 0) {
    alert("You’ve reached your story limit. Upgrade to continue!");
    return;
  }

  setGenerating(true);
  navigate(
    `/storyappuser?profile=${encodeURIComponent(
      userDoc?.fullName || userDoc?.email || "Reader"
    )}&diversity=${diversityMode}`
  );
  setGenerating(false);
};



  const handleDeleteRecording = async (rec) => {
    if (!window.confirm("Delete this recording?")) return;
    try {
      const storage = getStorage();
      if (rec.fileName || rec.storagePath) {
        await deleteObject(ref(storage, rec.fileName || rec.storagePath));
      }
      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { recordings: arrayRemove(rec) });
    } catch (err) {
      console.error("Delete recording error:", err);
      alert("Failed to delete recording.");
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  // UI helpers (guard against nulls)
  const cap = usage.cap ?? "—";
  const used = usage.used ?? "—";
  const remainingAddons = usage.remainingAddons ?? "—";
  const totalRemaining = usage.remainingTotal ?? "—";

  const progressPct =
    typeof usage.cap === "number" && typeof usage.used === "number" && usage.cap > 0
      ? Math.min(100, Math.round((usage.used / usage.cap) * 100))
      : 0;

  const nearEmpty =
    typeof usage.remainingTotal === "number" && usage.remainingTotal > 0 && usage.remainingTotal <= 3;

  const expDays =
  effectiveCurrentPeriodEnd != null
    ? Math.ceil((effectiveCurrentPeriodEnd.getTime() - Date.now()) / 86400000)
    : null;

  const nearExpiry = expDays != null && expDays <= 5;

  if (userLoading) return <p style={{ padding: 16 }}>Loading...</p>;

  return (
  <div
    className="full-screen-wrapper"
    style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      overflow: "auto",
      width: "100%",
      backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "center center",
      backgroundAttachment: isMobile ? "scroll" : "fixed",
      fontFamily: "Montserrat, sans-serif",
    }}
  >

        {/* Header */}
        <header className="page-header">
          <div className="logo">
            <img
              src={require("../assets/images/logo_name.PNG")}
              alt="YourStoryWorld Logo"
              style={{ height: 50, cursor: "pointer" }}
              onClick={() => navigate("/")}
            />
          </div>
          <nav style={{ display: "flex", gap: 15, justifyContent: "flex-end" }}>
            <button onClick={() => navigate("/help")} className="header-button">
              Help
            </button>
            <button onClick={() => navigate("/")} className="header-button">
              Home
            </button>
            <button
              onClick={handleLogout}
              className="header-button"
              style={{ backgroundColor: "blue", color: "white", fontWeight: "bold" }}
            >
              Logout
            </button>
          </nav>
        </header>


{isFreePlan && (
  <div
    style={{
      margin: "0 20px 10px",
      padding: "10px 16px",
      borderRadius: 10,
      background: "linear-gradient(90deg,#fde68a,#f97316)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
    }}
  >
    <div style={{ fontSize: 14 }}>
      <strong>Free plan: 5 stories per month.</strong>
      <div>Upgrade to unlock more stories and longer adventures.</div>
    </div>
    <button
      onClick={() => navigate("/subscriptions")}
      style={{
        padding: "8px 14px",
        borderRadius: 999,
        border: "none",
        background: "#1d4ed8",
        color: "white",
        fontWeight: 600,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      View Plans
    </button>
  </div>
)}


        {/* Main */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            padding: isMobile ? 10 : 20,
            gap: 20,
            alignItems: "flex-start",
          }}
        >
          {/* Sidebar */}
          <aside
            style={{
              width: isMobile ? "100%" : 280,
              background: "rgba(230,245,255,0.95)",
              padding: 20,
              borderRadius: 10,
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              marginBottom: isMobile ? 20 : 0,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div>
              <h2 style={{ color: "#007BFF", marginBottom: 6 }}>
                {userDoc?.fullName || userDoc?.email || "User"}
              </h2>

              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                Plan: {usage.planLabel || "Free"}{" "}
                {usage.billing && usage.billing !== "—" ? `(${usage.billing})` : ""}
              </div>

              <div style={{ fontSize: 12, margin: "8px 0 4px" }}>
  Stories this {isFreePlan ? "month" : "billing period"}: {used}/{cap}
</div>

              <div
                aria-label="included usage"
                style={{ height: 8, background: "#e0e7ff", borderRadius: 999, overflow: "hidden" }}
              >
                <div style={{ width: `${progressPct}%`, height: "100%", background: "#3b82f6" }} />
              </div>

              <div style={{ fontSize: 12, marginTop: 8 }}>
                Add-ons remaining: <strong>{remainingAddons}</strong>
              </div>
              <div style={{ fontSize: 12 }}>
                Total remaining: <strong>{totalRemaining}</strong>
              </div>

              <div style={{ fontSize: 12, marginTop: 10 }}>
  {isFreePlan ? (
  <>
    Monthly allowance renews on:{" "}
    <strong>{formatDate(effectiveCurrentPeriodEnd)}</strong>
    <div style={{ fontSize: 11, opacity: 0.9, marginTop: 4 }}>
      You get <strong>{cap}</strong> new stories every month from your sign-up date.
    </div>
  </>
) : (
  <>
    Renews: <strong>{formatDate(effectiveCurrentPeriodEnd)}</strong>
    {nearExpiry && (
      <div style={{ color: "#b45309", marginTop: 4 }}>
        ⏳ Renewal soon ({expDays} day{expDays === 1 ? "" : "s"})
      </div>
    )}
  </>
)}

</div>

            </div>

            {nearEmpty && isFreePlan && (
  <div
    role="alert"
    style={{
      background: "#fff7ed",
      border: "1px solid #fed7aa",
      color: "#9a3412",
      padding: 10,
      borderRadius: 8,
      fontSize: 13,
      display: "flex",
      flexDirection: "column",
      gap: 6,
    }}
  >
    <div>
      <strong>Almost out of free stories</strong>
      <div>
        You’ve used most of your <strong>5 free stories</strong> for this month.
      </div>
    </div>
    <button
      onClick={() => navigate("/subscriptions")}
      style={{
        alignSelf: "flex-start",
        padding: "6px 12px",
        borderRadius: 6,
        border: "none",
        background: "#1d4ed8",
        color: "white",
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      Upgrade to keep reading
    </button>
  </div>
)}


            <div style={{ marginTop: 12, background: "#f4f4f4", padding: 10, borderRadius: 8 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  gap: 10,
                  fontSize: 14,
                }}
              >
                <input
                  type="checkbox"
                  checked={diversityMode}
                  onChange={() => {
                    const v = !diversityMode;
                    setDiversityMode(v);
                    localStorage.setItem("diversityMode", String(v));
                  }}
                />
                <span>Enable Diversity Mode</span>
              </label>
              <p style={{ fontSize: 12, color: "#555", marginTop: 6 }}>
                Make characters, settings and themes reflect diverse cultures and backgrounds.
              </p>
            </div>
          </aside>

          {/* Main content */}
          <div style={{ flex: 1 }}>
            <h1 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF", textAlign: "center" }}>
            Welcome to Your Dashboard
          </h1>

            {/* Generate Story */}
            <div
              style={{
                background: "rgba(255,255,255,0.9)",
                padding: 30,
                borderRadius: 10,
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                textAlign: "center",
                marginBottom: 20,
              }}
            >
              <h2>Click the button below to start creating your stories!</h2>
              <button
  onClick={handleGenerateStory}
  disabled={generating || usageLoading || (usage.remainingTotal ?? 0) <= 0}
  style={{
    padding: "15px 30px",
    backgroundColor:
      generating || (usage.remainingTotal ?? 0) <= 0 ? "#9ca3af" : "#007BFF",
    color: "white",
    border: "none",
    borderRadius: 5,
    fontSize: "1rem",
    cursor: generating || (usage.remainingTotal ?? 0) <= 0 ? "not-allowed" : "pointer",
    marginTop: 10,
  }}
  title={
    (usage.remainingTotal ?? 0) <= 0
      ? "No stories left this month. Upgrade to continue."
      : "Create a Story"
  }
>
  {generating
    ? "Starting…"
    : (usage.remainingTotal ?? 0) <= 0
    ? "Limit Reached"
    : "Create a Story"}
</button>


              {(usage.remainingTotal ?? 0) <= 0 && (
  <div
    style={{
      marginTop: 10,
      fontSize: 13,
      color: "#b91c1c",
      background: "#fee2e2",
      border: "1px solid #fecaca",
      padding: "10px 12px",
      borderRadius: 8,
    }}
  >
    <strong>No free stories left this month.</strong>
    <div style={{ marginTop: 4 }}>
      Upgrade to a paid plan to keep creating new adventures without waiting for your next monthly reset.
    </div>
    <button
      onClick={() => navigate("/subscriptions")}
      style={{
        marginTop: 8,
        padding: "6px 12px",
        borderRadius: 6,
        border: "none",
        background: "#1d4ed8",
        color: "white",
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      See upgrade options
    </button>
  </div>
)}

            </div>

            {/* Reading Summary */}
            <div
              style={{
                background: "linear-gradient(to right, #2C3E50, #4CA1AF)",
                padding: 20,
                borderRadius: 10,
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                textAlign: "center",
                marginBottom: 20,
                color: "white",
              }}
            >

              <div style={{ marginTop: 10, display: "flex", justifyContent: "center", gap: 8 }}>
  <button
    onClick={() => setSummaryRange("month")}
    style={{
      padding: "4px 10px",
      borderRadius: 999,
      border: summaryRange === "month" ? "2px solid #F9C74F" : "1px solid #ccc",
      background: summaryRange === "month" ? "#F9C74F" : "white",
      fontSize: 12,
      cursor: "pointer",
    }}
  >
    This month
  </button>
  <button
    onClick={() => setSummaryRange("all")}
    style={{
      padding: "4px 10px",
      borderRadius: 999,
      border: summaryRange === "all" ? "2px solid #F9C74F" : "1px solid #ccc",
      background: summaryRange === "all" ? "#F9C74F" : "white",
      fontSize: 12,
      cursor: "pointer",
    }}
  >
    All time
  </button>
</div>

              <h2>📊 Reading Summary</h2>
              <p style={{ fontSize: 14, opacity: 0.9 }}>A quick overview of your reading progress.</p>

              {(() => {
                const raw = Array.isArray(userDoc?.readingActivity) ? userDoc.readingActivity : [];

let arr = raw;

 // 🔁 Use the *effective* period end (includes free-plan fallback)
  if (summaryRange === "month") {
  const end = new Date();           // now
  const start = new Date();
  start.setDate(start.getDate() - 30); // last 30 days

  arr = raw.filter((e) => {
    const d = new Date(e.date);
    if (isNaN(d.getTime())) return false; // guard against bad dates
    return d >= start && d <= end;
  });
}


                const stories = arr.reduce((s, e) => s + (e?.storiesGenerated || 0), 0);
                const totalTime = arr.reduce((s, e) => s + (e?.timeSpent || 0), 0);
                const avg = stories > 0 ? Math.round(totalTime / stories / 60) : 0;
                const fgRaw = userDoc?.favoriteGenres || {};

// ✅ Keep only entries where the value is a *number* (so we ignore things like 0: "Comedy")
const fgEntries = Object.entries(fgRaw).filter(
  ([key, val]) => typeof val === "number" && val > 0
);

// ✅ Safely pick the top genre
const popular =
  fgEntries.length > 0
    ? fgEntries.sort((a, b) => b[1] - a[1])[0][0]
    : "N/A";


                return (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                      marginTop: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <h3 style={{ color: "#F9C74F", fontSize: "1.8rem" }}>📖 {stories}</h3>
                      <p>Stories Generated</p>
                    </div>
                    <div>
                      <h3 style={{ color: "#F94144", fontSize: "1.8rem" }}>
                        ⏳ {Math.round(totalTime / 60)} min
                      </h3>
                      <p>Total Reading Time</p>
                    </div>
                    <div>
                      <h3 style={{ color: "#90BE6D", fontSize: "1.8rem" }}>📊 {avg} min</h3>
                      <p>Avg. Reading Time</p>
                    </div>
                    <div>
                      <h3 style={{ color: "#F3722C", fontSize: "1.8rem" }}>🌟 {popular}</h3>
                      <p>Most Popular Genre</p>
                    </div>
                  </div>
                );
              })()}

{isFreePlan && (
  <div
    style={{
      marginTop: 14,
      padding: "10px 14px",
      borderRadius: 8,
      background: "linear-gradient(90deg, #fef3c7, #fde68a)",
      border: "1px solid #fcd34d",
      color: "#92400e",
      fontSize: 14,
      fontWeight: 600,
      textAlign: "center",
      cursor: "pointer",
      boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    }}
    onClick={() => navigate("/subscriptions")}
  >
    📚 Get more stories & full personal or family dashboards — <u>Upgrade now</u>
  </div>
)}


            </div>



            {/* Tabs header */}
            <div
              style={{
                background: "linear-gradient(to right, #2C3E50, #4CA1AF)",
                padding: 15,
                borderRadius: 10,
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                marginBottom: 20,
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                indicatorColor="primary"
                textColor="primary"
                centered={!isMobile}
                variant={isMobile ? "scrollable" : "standard"}
                scrollButtons={isMobile ? "auto" : false}
                allowScrollButtonsMobile
                sx={{
                  "& .MuiTabs-flexContainer": { justifyContent: "center" },
                  "& .MuiTab-root": {
                    fontWeight: "bold",
                    fontSize: "16px",
                    color: "white",
                    textTransform: "none",
                  },
                  "& .Mui-selected": { color: "#007BFF !important" },
                }}
              >
                <Tab label="📈 Progress Overview" />
                <Tab label="🔥 Reading Habits" />
                <Tab label="⏳ Reading/Comprehension Insights" />
                <Tab label="🎙️ Recordings" />
              </Tabs>
            </div>

            {/* Tab content */}
            <div style={{ marginTop: 20 }}>
              {activeTab === 0 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: 20,
                  }}
                >
                  <div
                    style={{
                      background: "rgba(255,255,255,0.9)",
                      padding: 20,
                      borderRadius: 10,
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      textAlign: "center",
                      height: 300,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <h2 style={{ fontSize: 14, marginBottom: 5 }}>📈 Daily Stories Generated</h2>
                    <p style={{ color: "#555", fontSize: 12 }}>Stories created over the last 7 days</p>
                    <div style={{ height: 220, position: "relative" }}>
                      <Bar data={storiesReadData} options={{ responsive: true, maintainAspectRatio: false }} />
                    </div>
                  </div>

                  <div
                    style={{
                      background: "rgba(255,255,255,0.9)",
                      padding: 20,
                      borderRadius: 10,
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      textAlign: "center",
                      height: 300,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <h2 style={{ fontSize: 14, marginBottom: 5 }}>🔥 Total Reading Time</h2>
                    <p style={{ color: "#555", fontSize: 12 }}>Minutes read per day</p>
                    <div style={{ height: 220, position: "relative" }}>
                      <Line data={timeSpentReadingData} options={{ responsive: true, maintainAspectRatio: false }} />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 1 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: 20,
                  }}
                >
                  <div
                    style={{
                      background: "rgba(255,255,255,0.9)",
                      padding: 20,
                      borderRadius: 10,
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      textAlign: "center",
                      height: 300,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <h2 style={{ fontSize: 14, marginBottom: 5 }}>🔥 Weekly Reading Streak</h2>
                    <p style={{ color: "#555", fontSize: 12 }}>Days read each week</p>
                    <div style={{ height: 220, position: "relative" }}>
                      <Bar
                        data={readingStreaksData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: { beginAtZero: true, max: 7, title: { display: true, text: "Days" } },
                            x: { title: { display: true, text: "Week" } },
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      background: "rgba(255,255,255,0.9)",
                      padding: 20,
                      borderRadius: 10,
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      textAlign: "center",
                      height: 300,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <h2 style={{ fontSize: 14, marginBottom: 5 }}>🌟 Favorite Genres</h2>
                    <p style={{ color: "#555", fontSize: 12 }}>Most selected genres</p>
                    <div style={{ height: 220, position: "relative" }}>
                      <Bar data={favoriteGenresData} options={{ responsive: true, maintainAspectRatio: false }} />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 2 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: 20,
                  }}
                >
                  <div
                    style={{
                      background: "rgba(255,255,255,0.9)",
                      padding: 20,
                      borderRadius: 10,
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      textAlign: "center",
                      height: 300,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <h2 style={{ fontSize: 14, marginBottom: 5 }}>⏳ Average Reading Time</h2>
                    <p style={{ color: "#555", fontSize: 12 }}>
                      How long you typically spend reading per session.
                    </p>
                    <div style={{ height: 220, position: "relative" }}>
                      <Line data={averageReadingTimeData} options={{ responsive: true, maintainAspectRatio: false }} />
                    </div>
                  </div>

                  <div
                    style={{
                      background: "rgba(255,255,255,0.9)",
                      padding: 20,
                      borderRadius: 10,
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      textAlign: "center",
                      height: 300,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <h2 style={{ fontSize: 14, marginBottom: 5 }}>🧠 Comprehension Scores</h2>
                    <p style={{ color: "#555", fontSize: 12 }}>Your comprehension quiz performance.</p>
                    <div style={{ height: 220, position: "relative" }}>
                      <Line
                        data={{
                          labels: (userDoc?.comprehensionScores || []).map((e) => {
                            const d = new Date(e.date);
                            return `${d.getDate()} ${d.toLocaleString("default", { month: "short" })}`;
                          }),
                          datasets: [
                            {
                              label: "Comprehension Score (%)",
                              data: (userDoc?.comprehensionScores || []).map((e) => e.score || 0),
                              backgroundColor: "rgba(153,102,255,0.5)",
                              borderColor: "rgba(153,102,255,1)",
                              borderWidth: 2,
                              fill: false,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              title: { display: true, text: "Score (%)" },
                              beginAtZero: true,
                              max: 100,
                            },
                            x: { title: { display: true, text: "Date" } },
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 3 && (
                <div
                  style={{
                    background: "rgba(255,255,255,0.95)",
                    padding: 20,
                    borderRadius: 10,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  <h2 style={{ marginBottom: 10 }}>🎧 Saved Read-Aloud Recordings</h2>

                  {!userDoc?.recordings || userDoc.recordings.length === 0 ? (
                    <div style={{ textAlign: "center", marginTop: 30 }}>
                      <p style={{ color: "#777", marginBottom: 20 }}>
                        No recordings saved yet. Record yourself reading a story to see them here!
                      </p>
                    </div>
                  ) : (
                    <>
                      {userDoc.recordings.map((rec, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom: 15,
                            borderBottom: "1px solid #ccc",
                            paddingBottom: 10,
                          }}
                        >
                          <p>
                            <strong>{rec.storyTitle || rec.fileName || `Recording ${idx + 1}`}</strong>
                          </p>
                          <p style={{ fontSize: "0.9em", color: "#555" }}>
                            Uploaded: {rec.createdAt ? new Date(rec.createdAt).toLocaleString() : "Unknown"}
                          </p>
                          <audio controls src={rec.url} style={{ width: "100%", marginTop: 8 }} />

                          <button
                            onClick={() => handleDeleteRecording(rec)}
                            style={{
                              marginTop: 10,
                              backgroundColor: "#ff4d4d",
                              color: "white",
                              padding: "6px 12px",
                              borderRadius: 5,
                              border: "none",
                              cursor: "pointer",
                              marginRight: 10,
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="page-footer">
          <p>&copy; {new Date().getFullYear()} YourStoryWorld. All rights reserved.</p>
          <nav style={{ marginTop: 10 }}>
            <a href="/about" className="footer-link">
              About Us
            </a>
            <a href="/contact" className="footer-link">
              Contact
            </a>
          </nav>
        </footer>
      </div>
  );
};

export default FreeDashboard;
